/**
 * TopicPostGenerationDialog (Sprint 16) — generate N posts from a single topic.
 *
 * Opened from the Topic Detail panel's "Generate posts" primary action. Unlike
 * the sprint-scoped Stage C flow, this dialog works WITHOUT a strategy — posts
 * can land as topic drafts or as entries in the project-scoped Post Examples
 * library. When the user explicitly picks "Add to sprint", they also pick the
 * target strategy + sprint inline.
 *
 * Fires `POST /api/agent/content-plan/generate-posts` with:
 *   - scope: 'topic', topicIds: [topic.id]
 *   - platforms: multi-select
 *   - postsPerPlatform: 1..10
 *   - asSeries: toggle → sequential variants with prior-post context
 *   - seriesFlavor: narrative mode when asSeries=true
 *   - intendedDestination: advisory pick (final destination chosen at apply)
 *
 * Returns immediately; drafts appear in the Post Generation History section
 * of the Topic Detail and flow through the Sprint 15 review-and-apply pipeline.
 */

import React, { useState } from 'react';
import {
  Stack, Box, Typography, TextField, Alert, Slider, InputAdornment,
  FormControlLabel, Switch, Chip, MenuItem, Select, InputLabel, FormControl,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { BaseDialog, DialogHeader, DialogContent, DialogFooter } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import type { PlatformId, ContentTopic } from '../types';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import { useTopicResearchRuns } from '../hooks/useTopicResearchRuns';
import { useAuthor } from '@modules/content-plan/hooks/useAuthor';
import { useAuthorPlatforms } from '@modules/content-plan/hooks/useAuthorPlatforms';
import { useProjectDefaultModel } from '@modules/content-plan/hooks/useProjectDefaultModel';
import {
  ResearchScopeSelector,
  buildResearchSelection,
  createResearchScopeState,
  type ResearchScopeSelectorState,
} from './research/ResearchScopeSelector';

const TESTIDS = {
  dialog: 'topic-post-generation-dialog',
  platforms: 'topic-post-platforms',
  postsPerPlatform: 'topic-post-count',
  postsPerPlatformInput: 'topic-post-count-input',
  asSeries: 'topic-post-as-series',
  seriesFlavor: 'topic-post-series-flavor',
  destination: 'topic-post-destination',
  modelSelector: 'topic-post-model',
  submit: 'topic-post-submit',
  cancel: 'topic-post-cancel',
} as const;

export type PostDestination = 'sprint' | 'topic_drafts' | 'post_examples';
export type SeriesFlavor = 'continuation' | 'counter_point' | 'how_to_sequence' | 'story_arc';

export interface TopicPostGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string;
  topic: ContentTopic;
  /** When present, "To sprint" destination is enabled. */
  strategyId?: string;
  sprintId?: string;
  onSuccess?: (result: { taskId: string }) => void;
}

export const TopicPostGenerationDialog: React.FC<TopicPostGenerationDialogProps> = ({
  open, onClose, projectId, userId, authorId, topic, strategyId, sprintId, onSuccess,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const { author } = useAuthor(projectId);
  const { platforms: authorPlatforms } = useAuthorPlatforms(projectId, authorId || author?.id || null);
  const selectablePlatforms: PlatformId[] = React.useMemo(
    () => authorPlatforms
      .filter((platform) => platform.isActive !== false)
      .map((platform) => platform.id as PlatformId),
    [authorPlatforms],
  );
  const defaultPlatforms: PlatformId[] = React.useMemo(() => {
    const selectable = new Set(selectablePlatforms.map(String));
    if (topic.targetPlatforms && topic.targetPlatforms.length > 0) {
      return (topic.targetPlatforms as PlatformId[]).filter((platform) => selectable.has(String(platform)));
    }
    return selectablePlatforms;
  }, [topic.targetPlatforms, selectablePlatforms]);

  const [platforms, setPlatforms] = useState<PlatformId[]>([]);
  const [postsPerPlatform, setPostsPerPlatform] = useState<number>(1);
  const [asSeries, setAsSeries] = useState<boolean>(false);
  const [seriesFlavor, setSeriesFlavor] = useState<SeriesFlavor>('continuation');
  const [destination, setDestination] = useState<PostDestination>(
    strategyId ? 'sprint' : 'topic_drafts',
  );
  const [model, setModel] = useState<string>(projectDefaultModel.value);
  // Sprint 16 Part C — asset mode: do both / plan only / skip assets entirely.
  // v1.8.0: default is fetched from the backend env-resolver (assetModeResolver)
  // so debug deploys default to plan_only and prod to plan_and_generate without
  // requiring a frontend rebuild. Fallback to plan_and_generate when the API
  // call fails so existing UX is unchanged.
  const [assetMode, setAssetMode] = useState<'plan_and_generate' | 'plan_only' | 'text_only'>('plan_and_generate');
  const [assetModeUserTouched, setAssetModeUserTouched] = useState(false);
  const [userInstruction, setUserInstruction] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Task 4 — granular research selector. Each run can be toggled, and within
  // a run the user can untick analysis fields, pick raw chunks, or include
  // the full Phase 2 report. Default: active completed runs selected.
  const { runs: topicRuns, loading: runsLoading } = useTopicResearchRuns(
    open ? projectId : null,
    open ? topic.id : null,
    authorId,
  );
  const [researchScope, setResearchScope] = useState<ResearchScopeSelectorState>(
    () => createResearchScopeState([]),
  );

  React.useEffect(() => {
    if (!open) return;
    // Reset to topic's defaults on (re-)open so prior state doesn't leak.
    setPlatforms(defaultPlatforms);
    setPostsPerPlatform(1);
    setAsSeries(false);
    setSeriesFlavor('continuation');
    setDestination(strategyId ? 'sprint' : 'topic_drafts');
    setModel(projectDefaultModel.value);
    setAssetMode('plan_and_generate');
    setAssetModeUserTouched(false);
    setUserInstruction('');
    setSubmitting(false);
    setError(null);
    setResearchScope(createResearchScopeState([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultPlatforms, strategyId, projectDefaultModel.value]);

  // v1.8.0: pre-fill the assetMode default from the backend env-resolver.
  // Skips when the user already changed the dropdown.
  React.useEffect(() => {
    if (!open || assetModeUserTouched) return;
    let cancelled = false;
    (async () => {
      try {
        const client = new ContentPlanApiClient();
        const r = await client.getAssetModeDefault();
        if (cancelled || assetModeUserTouched) return;
        if (r?.mode && r.mode !== assetMode) setAssetMode(r.mode);
      } catch {
        // Silent — fallback to existing default.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-select active completed runs by default (user can override).
  React.useEffect(() => {
    if (!open || runsLoading) return;
    setResearchScope((prev) => prev.touched ? prev : createResearchScopeState(topicRuns));
  }, [open, runsLoading, topicRuns]);

  const togglePlatform = (p: PlatformId) => {
    setPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  // Series mode requires >1 post per platform — auto-disable when count drops.
  React.useEffect(() => {
    if (postsPerPlatform <= 1 && asSeries) setAsSeries(false);
  }, [postsPerPlatform, asSeries]);

  const totalPosts = platforms.length * Math.max(1, postsPerPlatform);
  const canSubmit = !submitting && platforms.length > 0 && totalPosts > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const client = new ContentPlanApiClient();
      const researchSelection = buildResearchSelection(researchScope);
      const resp = await client.generatePosts({
        projectId, userId,
        authorId,
        ...(strategyId && destination === 'sprint' ? { strategyId } : {}),
        ...(sprintId && destination === 'sprint' ? { sprintId } : {}),
        scope: 'topic',
        topicIds: [topic.id],
        platforms: platforms as unknown as string[],
        postsPerPlatform,
        asSeries,
        ...(asSeries ? { seriesCount: postsPerPlatform, seriesFlavor } : {}),
        intendedDestination: destination,
        // Sprint 16 Part C — asset pipeline flags.
        skipAssetPlanning: assetMode === 'text_only',
        generateAssetsImmediately: assetMode === 'plan_and_generate',
        userInstruction: userInstruction.trim() || undefined,
        models: { platformPost: model },
        // Task 4 — granular research-to-post selector. When user changed
        // defaults, scope research per topic; otherwise omit (loader falls
        // back to all runs / all Phase-3 fields).
        ...(researchSelection
          ? { researchSelectionByTopic: { [topic.id]: researchSelection } }
          : {}),
      } as any);
      if (!resp.success) throw new Error(resp.error ?? 'Post generation failed');
      onSuccess?.({ taskId: resp.taskId });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} maxWidth="sm" data-testid={TESTIDS.dialog}>
      <DialogHeader
        title="Generate posts for this topic"
        subtitle={topic.angle}
        onClose={onClose}
      />
      {/* Stable height — avoids reflow when destination radio expands the
          strategy picker hint. */}
      <DialogContent sx={{ minHeight: 520, overflow: 'auto' }}>
        <Stack spacing={2.5} sx={{ py: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Platforms */}
          <Box data-testid={TESTIDS.platforms}>
            <Typography variant="subtitle2" gutterBottom>Target platforms</Typography>
            {selectablePlatforms.length === 0 && platforms.length === 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                No active author platforms are configured. Add a platform to the author before generating posts.
              </Alert>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectablePlatforms.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  onClick={() => togglePlatform(p)}
                  color={platforms.includes(p) ? 'primary' : 'default'}
                  variant={platforms.includes(p) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
            {platforms.length === 0 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                Pick at least one platform.
              </Typography>
            )}
          </Box>

          {/* Posts per platform */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Posts per platform</Typography>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 1, px: 1 }}>
              <TextField
                type="number"
                value={postsPerPlatform}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (Number.isNaN(n)) return;
                  setPostsPerPlatform(Math.max(1, Math.min(10, n)));
                }}
                inputProps={{
                  min: 1, max: 10, step: 1,
                  'data-testid': TESTIDS.postsPerPlatformInput,
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">posts</InputAdornment>,
                }}
                size="small"
                sx={{ width: 140, flexShrink: 0 }}
              />
              <Slider
                value={postsPerPlatform}
                onChange={(_, v) => setPostsPerPlatform(v as number)}
                step={1} min={1} max={10}
                valueLabelDisplay="auto"
                data-testid={TESTIDS.postsPerPlatform}
                sx={{ flex: 1, mx: 1 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Will produce <strong>{totalPosts}</strong> post{totalPosts === 1 ? '' : 's'} total
              ({platforms.length} platform{platforms.length === 1 ? '' : 's'} × {postsPerPlatform}).
            </Typography>
          </Box>

          {/* Series mode */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={asSeries}
                  onChange={(e) => setAsSeries(e.target.checked)}
                  disabled={postsPerPlatform <= 1}
                  data-testid={TESTIDS.asSeries}
                />
              }
              label="Generate as a narrative series"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
              {postsPerPlatform <= 1
                ? 'Enable by setting posts per platform > 1.'
                : 'Each post gets prior posts injected into the prompt — AI builds continuity instead of independent variants.'}
            </Typography>
            {asSeries && (
              <Box sx={{ mt: 1 }} data-testid={TESTIDS.seriesFlavor}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Narrative flavor
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={seriesFlavor}
                  onChange={(_, v) => { if (v) setSeriesFlavor(v as SeriesFlavor); }}
                >
                  <ToggleButton value="continuation">Continuation</ToggleButton>
                  <ToggleButton value="counter_point">Counter-point</ToggleButton>
                  <ToggleButton value="how_to_sequence">How-to sequence</ToggleButton>
                  <ToggleButton value="story_arc">Story arc</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>

          {/* Destination */}
          <Box data-testid={TESTIDS.destination}>
            <FormControl fullWidth size="small">
              <InputLabel id="topic-post-destination-label">Destination (advisory)</InputLabel>
              <Select
                labelId="topic-post-destination-label"
                label="Destination (advisory)"
                value={destination}
                onChange={(e) => setDestination(e.target.value as PostDestination)}
              >
                <MenuItem value="sprint" disabled={!strategyId}>
                  To sprint {strategyId ? '' : '(pick a strategy first)'}
                </MenuItem>
                <MenuItem value="topic_drafts">As topic drafts</MenuItem>
                <MenuItem value="post_examples">Add to Post Examples library</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              You'll confirm (or change) the destination at the Apply step after review.
            </Typography>
          </Box>

          {/* Assets mode (Sprint 16 Part C) */}
          <Box data-testid="topic-post-assets-mode">
            <Typography variant="subtitle2" gutterBottom>Assets</Typography>
            <ToggleButtonGroup
              exclusive size="small"
              value={assetMode}
              onChange={(_, v) => {
                if (!v) return;
                const prev = assetMode;
                setAssetMode(v as typeof assetMode);
                setAssetModeUserTouched(true);
                // v1.8.0 telemetry — canonical event name, do not rename.
                import('../telemetry/designBriefEvents')
                  .then((m) => m.emitAssetModeChanged({ source: 'ui', newValue: v as typeof assetMode, oldValue: prev }))
                  .catch(() => undefined);
              }}
            >
              <ToggleButton value="plan_and_generate">Plan + generate</ToggleButton>
              <ToggleButton value="plan_only">Plan only</ToggleButton>
              <ToggleButton value="text_only">Text only</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {assetMode === 'plan_and_generate' && 'AI picks slots + writes prompts + runs Gemini image gen for each slot.'}
              {assetMode === 'plan_only' && 'AI plans slots + prompts; you click Generate per-slot later in the editor.'}
              {assetMode === 'text_only' && 'No asset planning — posts land text-only; add assets later manually.'}
            </Typography>
            {assetMode !== 'text_only' && (
              <TextField
                label="Asset instruction (optional)"
                value={userInstruction}
                onChange={(e) => setUserInstruction(e.target.value)}
                multiline minRows={2} maxRows={5} fullWidth size="small"
                sx={{ mt: 1 }}
                placeholder="E.g. '3 carousel slides, photorealistic product shots, cohesive palette'"
                helperText="Override the planner's platform defaults with your own rules"
              />
            )}
          </Box>

          {/* Task 4 — Research scope selector */}
          {topicRuns.length > 0 && (
            <Box data-testid="topic-post-research-scope">
              <ResearchScopeSelector
                runs={topicRuns}
                state={researchScope}
                onChange={setResearchScope}
              />
            </Box>
          )}

          {/* Model */}
          <Box data-testid={TESTIDS.modelSelector}>
            <Typography variant="subtitle2" gutterBottom>Model</Typography>
            <AvailableModelSelector
              projectId={projectId}
              value={model}
              onChange={setModel}
              label="Platform-post model"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel', onClick: onClose, 'data-testid': TESTIDS.cancel,
        } as any}
        primaryAction={{
          label: submitting
            ? 'Starting…'
            : `Generate ${totalPosts} post${totalPosts === 1 ? '' : 's'}`,
          onClick: handleSubmit,
          disabled: !canSubmit,
          startIcon: <AutoFixHighIcon />,
          'data-testid': TESTIDS.submit,
        } as any}
      />
    </BaseDialog>
  );
};

export default TopicPostGenerationDialog;
